import { Delete } from "@mui/icons-material";
import { Button, IconButton, Tooltip, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CreateMailingModal from "../components/CreateMailingModal";
import CustomTable from "../components/CustomTable";
import DataSelect from "../components/DataInputs/DataSelect";
import DataSwitch from "../components/DataInputs/DataSwitch";
import DataTextField from "../components/DataInputs/DataTextField";
import FilterBase from "../components/Filter";
import Periods from "../Enums/Periods";
import usePagination, { InfinityPagination } from "../hooks/Pagination";
import { LetterFilter, LetterSearch, LetterTable } from "../interfaces/Letter";
import { LetterTemplateTable } from "../interfaces/LetterTemplate";
import {
	ArrayContainer,
	Filter,
	Pagination,
	ResponseContainer,
	Sort,
} from "../interfaces/RequestBase";
import { TableTemplate } from "../interfaces/Table";
import {
	DeleteLetter,
	GetLetters,
	GetLetterTemplates,
} from "../requester/functions";
import dateToHuman from "../utilities/date";
import GroupsIcon from "@mui/icons-material/Groups";
import getFilter from "../utilities/filter";
import { NotEmpty } from "../utilities/validate";
import ListModal from "../components/ListModal";

interface IProps {}

const MailingPage = (props: IProps) => {
	const [letters, setLetters] = useState<LetterTable[]>([]);
	const [pagination, setPage, setPageSize] = usePagination();
	const [filter, setFilter] = useState<LetterFilter>({});
	const [search, setSearch] = useState<LetterSearch>({});
	const [sort, setSort] = useState<Sort>({});
	const [totalCount, setTotalCount] = useState<number>(0);

	const [openModal, setOpenModal] = useState<boolean>(false);
	const [templates, setTemplates] = useState<LetterTemplateTable[]>([]);

	const renderManager = (item: any, index: number) => (
		<div
			key={`candidate-${index}`}
			className="flex items-center w-full gap-3 rounded-lg px-1 py-1"
		>
			<Typography>{item.full_name}</Typography>
		</div>
	);

	const rows: TableTemplate = {
		data: [
			{
				title: "Шаблон",
				sortKey: "template.name",
				render: (item: LetterTable) => item.template?.name || "Нет",
			},
			{
				title: "Создатель",
				sortKey: "manager_create.last_name",
				render: (item: LetterTable) => item.manager_create?.full_name || "Нет",
			},
			{
				title: "Дата и время отправки",
				sortKey: "start_date",
				render: (item: LetterTable) =>
					item.start_date ? dateToHuman(item.start_date) : "Нет",
			},
			{
				title: "Периодичность",
				sortKey: "period",
				render: (item: LetterTable) =>
					item.period
						? Periods.find((p) => p.timestamp === item.period)?.name
						: "Нет",
			},
			{
				title: "Вакансия",
				sortKey: "vacancy.name",
				render: (item: LetterTable) => item.vacancy?.name || "Нет",
			},
			{
				title: "Кандидаты",
				renderExport: (item: LetterTable) =>
					item.candidates?.map((c) => c.full_name).join(", ") || "Нет",
				render: (item: LetterTable) => (
					<ListModal data={item.candidates || []} renderElement={renderManager}>
						{NotEmpty(item.candidates) ? <GroupsIcon /> : "Нет"}
					</ListModal>
				),
			},
			{
				title: "",
				render: (item: LetterTable) => (
					<Tooltip title={"Удалить"}>
						<IconButton
							onClick={() => {
								DeleteLetter({ id: item.id, is_delete: false }, () => {});
								setFilter({ ...filter });
							}}
						>
							<Delete />
						</IconButton>
					</Tooltip>
				),
			},
		],
	};

	useEffect(() => {
		GetLetterTemplates(
			{
				pagination: InfinityPagination,
			},
			(resp) => {
				if (resp.status === "ok" && resp.data) {
					setTemplates(resp.data.data);
				}
			}
		);
	}, []);

	const getLettersExport = (callback: any) => {
		var req: Filter<LetterFilter, LetterSearch> = getFilter(
			{ page: 0, size: totalCount } as Pagination,
			filter,
			search,
			sort
		);
		GetLetters(req, (resp: ResponseContainer<ArrayContainer<LetterTable>>) => {
			if (resp.status === "ok" && resp.data) {
				callback(resp.data.data);
			}
		});
	};

	const getLetters = () => {
		var req: Filter<LetterFilter, LetterSearch> = getFilter(
			pagination as Pagination,
			filter,
			search,
			sort
		);
		GetLetters(req, (resp: ResponseContainer<ArrayContainer<LetterTable>>) => {
			if (resp.status === "ok" && resp.data) {
				setLetters(resp.data.data);
				setTotalCount(resp.data?.total_count || 0);
			}
		});
	};

	useEffect(() => {
		getLetters();
	}, [filter, pagination, search, sort]);

	return (
		<div className="flex flex-col gap-5">
			<div className="flex justify-between w-full">
				<Button
					onClick={() => {
						setOpenModal(true);
					}}
				>
					Создать
				</Button>
				<FilterBase setSearch={setSearch} setFilter={setFilter}>
					<DataTextField
						keyValue={"candidate_name"}
						value={search}
						setValue={setSearch}
						inputProps={{ label: "Поиск кандидата" }}
					/>
					<DataTextField
						keyValue={"manager_name"}
						value={search}
						setValue={setSearch}
						inputProps={{ label: "Поиск менеджера" }}
					/>
					<DataSelect
						keyValue={"id_template"}
						list={templates}
						value={filter}
						setValue={setFilter}
						label="Шаблон"
					/>
					<DataSwitch
						keyValue={"repeat"}
						value={filter}
						setValue={setFilter}
						label="Рассылка"
					/>
					<DataSwitch
						keyValue={"is_delete"}
						value={filter}
						setValue={setFilter}
						label="Удаленные"
					/>
				</FilterBase>
			</div>
			<CustomTable
				rows={rows}
				data={letters}
				allCount={totalCount}
				pagination={pagination as Pagination}
				setPage={setPage as (p: number) => void}
				setPageSize={setPageSize as (p: number) => void}
				sort={sort}
				setSort={setSort}
				onExport={getLettersExport}
			/>
			<CreateMailingModal
				open={openModal}
				onClose={() => {
					setOpenModal(false);
				}}
				onCreate={() => {
					getLetters();
				}}
			/>
		</div>
	);
};

export default MailingPage;
